/**
 * Org payout dashboard — enterprise admin console for the fleet manager.
 *
 * Shows the corporate bank account(s), earnings snapshot (MTD / YTD /
 * lifetime), and a data-dense payout history table. Intentionally uses
 * neutral tones + minimal animation to feel distinct from the consumer
 * host surface.
 *
 * Access: only the org's fleet_manager (legal representative) can see this
 * — enforced server-side by RLS, and by the CTA gate on the org dashboard.
 */
import { useCallback } from 'react'
import { View, Text, RefreshControl } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import Animated from 'react-native-reanimated'
import { Building2, CheckCircle2, Plus, Wallet, Receipt, XCircle } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { OrgScopeRibbon } from '@/components/hosting/OrgScopeRibbon'
import { useOrganization } from '@/lib/hooks/use-organization'
import {
  usePayoutAccountsForOrg,
  usePayoutHistoryForOrg,
  useOrgEarnings,
  useSetDefaultPayoutAccountForOrg,
} from '@/lib/hooks/use-payouts'
import { useSemanticColors, SPACING, RADIUS, INTERACTION } from '@/lib/theme/tokens'
import { FONTS, COLORS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { formatCurrency } from '@/lib/utils/format'
import { formatBookingDate } from '@/lib/utils/dates'
import { useEntrance } from '@/lib/hooks/use-entrance'

function formatDate(iso: string, locale: string): string {
  return formatBookingDate(iso, locale, { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function OrgPayoutsScreen() {
  const enter = useEntrance()
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const router = useRouter()
  const insets = useScreenInsets()
  const { orgId } = useLocalSearchParams<{ orgId: string }>()

  const { organization } = useOrganization(orgId)
  const {
    data: accounts = [],
    isLoading: accountsLoading,
    isError: accountsError,
    refetch: refetchAccounts,
  } = usePayoutAccountsForOrg(orgId)
  const {
    data: payouts = [],
    isLoading: payoutsLoading,
    isError: payoutsError,
    refetch: refetchPayouts,
  } = usePayoutHistoryForOrg(orgId)
  const {
    data: earnings,
    isLoading: earningsLoading,
    refetch: refetchEarnings,
  } = useOrgEarnings(orgId)
  const setDefault = useSetDefaultPayoutAccountForOrg(orgId)

  const defaultAccount = accounts.find((a) => a.isDefault) ?? accounts[0] ?? null
  const hasAccount = !!defaultAccount
  // Both the subtitle and the footnote interpolate the organisation name. While
  // the org query is in flight the name was an empty string and the sentence
  // still rendered — "Ganancias y cuentas bancarias de ." Nothing is better
  // than a stranded period, and the copy arrives a moment later.
  const orgName = organization?.legalName ?? organization?.name ?? null

  const goToOnboarding = useCallback(
    () => router.push(`/hosting/organization/payouts/onboarding?orgId=${orgId}` as never),
    [router, orgId],
  )

  const handleRefresh = useCallback(async () => {
    await Promise.all([refetchAccounts(), refetchPayouts(), refetchEarnings()])
  }, [refetchAccounts, refetchEarnings, refetchPayouts])

  const handleSetDefault = useCallback(
    async (accountId: string) => {
      try {
        await setDefault.mutateAsync(accountId)
        toast.success(t('payouts.default_set'))
      } catch (err) {
        toast.error(errorMessage(err, t('errors.generic')))
        captureError(err, { screen: 'org/payouts', action: 'set_default' }, { orgId, accountId })
      }
    },
    [orgId, setDefault, t],
  )

  return (
    <ScreenContainer variant="dashboard" scroll={false}>
      {/* Title and subtitle live in the header row, exactly like
          /hosting/organization and /hosting/organization/members. This screen
          used to leave the back button alone on its own row and restate the
          title as a 32pt h1 inside the scroll, so the same heading changed
          size and position while moving between the three org screens. */}
      <ScreenHeader
        title={t('org_payouts.title')}
        subtitle={orgName ? t('org_payouts.subtitle', { name: orgName }) : undefined}
      />
      {organization && (
        <OrgScopeRibbon
          orgName={organization.legalName ?? organization.name}
          isVerified={organization.isVerified}
        />
      )}

      <ScrollView
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={accountsLoading || payoutsLoading || earningsLoading}
            onRefresh={handleRefresh}
            tintColor={COLORS.coral.DEFAULT}
          />
        }
        contentContainerStyle={{
          paddingHorizontal: SPACING.lg,
          // The closing footnote was landing under the tab bar.
          paddingBottom: insets.bottom + SPACING['3xl'],
        }}
      >
        {/* ─── Earnings snapshot ─── */}
        <Animated.View
          collapsable={false}
          entering={enter()}
          style={{
            flexDirection: 'row',
            gap: SPACING.md,
            flexWrap: 'wrap',
          }}
        >
          <EarningsTile
            label={t('org_payouts.mtd')}
            value={earnings ? formatCurrency(earnings.mtdCents) : null}
            loading={earningsLoading}
            colors={colors}
          />
          <EarningsTile
            label={t('org_payouts.ytd')}
            value={earnings ? formatCurrency(earnings.ytdCents) : null}
            loading={earningsLoading}
            colors={colors}
          />
          <EarningsTile
            label={t('org_payouts.lifetime')}
            value={earnings ? formatCurrency(earnings.capturedTotalCents) : null}
            sublabel={
              earnings ? `${earnings.capturedCount} ${t('org_payouts.rentals')}` : undefined
            }
            loading={earningsLoading}
            colors={colors}
          />
          <EarningsTile
            label={t('org_payouts.pending')}
            value={earnings ? formatCurrency(earnings.pendingCents) : null}
            loading={earningsLoading}
            colors={colors}
          />
        </Animated.View>

        {/* ─── Primary account card ─── */}
        <SectionLabel text={t('org_payouts.primary_account')} colors={colors} />

        {accountsLoading ? (
          <Skeleton width="100%" height={120} borderRadius={RADIUS.lg} />
        ) : accountsError ? (
          <ErrorCard message={t('errors.generic')} onRetry={refetchAccounts} colors={colors} />
        ) : !hasAccount ? (
          <EmptyPanel
            icon={Wallet}
            title={t('org_payouts.configure_cta')}
            hint={t('org_payouts.configure_hint')}
            onPress={goToOnboarding}
            testID="org-payouts.add-account-cta"
            colors={colors}
          />
        ) : (
          <AccountCard
            bankName={defaultAccount.bankName ?? '—'}
            accountHolder={defaultAccount.accountHolderName ?? organization?.legalName ?? '—'}
            masked={defaultAccount.accountNumberMasked ?? '****'}
            verified={defaultAccount.isVerified}
            onEdit={goToOnboarding}
            colors={colors}
            t={t}
          />
        )}

        {/* Alternate accounts */}
        {accounts.length > 1 && (
          <View style={{ marginTop: SPACING.md, gap: SPACING.sm }}>
            <Text
              style={{
                fontSize: 12,
                fontFamily: FONTS.body,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
              }}
            >
              {t('org_payouts.other_accounts')}
            </Text>
            {accounts
              .filter((a) => a.id !== defaultAccount?.id)
              .map((a) => (
                <Pressable
                  key={a.id}
                  onPress={() => void handleSetDefault(a.id)}
                  disabled={setDefault.isPending}
                  accessibilityRole="button"
                  testID={`org-payouts.set-default-${a.id}`}
                  style={{
                    padding: SPACING.md,
                    borderRadius: RADIUS.md,
                    borderWidth: 1,
                    borderColor: colors.border,
                    backgroundColor: colors.surface,
                    opacity: setDefault.isPending ? 0.48 : 1,
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{
                        fontSize: 13,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {a.bankName ?? '—'} · {a.accountNumberMasked}
                    </Text>
                    <Text
                      style={{
                        fontSize: 12,
                        fontFamily: FONTS.body,
                        color: colors.textMuted,
                        marginTop: 2,
                      }}
                    >
                      {a.accountHolderName ?? '—'}
                    </Text>
                  </View>
                  <Text
                    style={{
                      fontSize: 12,
                      fontFamily: FONTS.bodyBold,
                      color: colors.textAccent,
                    }}
                  >
                    {t('org_payouts.set_default')}
                  </Text>
                </Pressable>
              ))}
          </View>
        )}

        {hasAccount && (
          <Pressable
            onPress={goToOnboarding}
            accessibilityRole="button"
            testID="org-payouts.add-another-cta"
            hitSlop={INTERACTION.iconHitSlop}
            style={{
              marginTop: SPACING.sm,
              padding: SPACING.sm,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              alignSelf: 'flex-start',
            }}
          >
            <Plus size={14} color={COLORS.coral.DEFAULT} strokeWidth={2} />
            <Text style={{ fontSize: 13, fontFamily: FONTS.bodyBold, color: colors.textAccent }}>
              {t('org_payouts.add_account')}
            </Text>
          </Pressable>
        )}

        {/* ─── Payouts table ─── */}
        <SectionLabel text={t('org_payouts.history')} colors={colors} />

        {payoutsLoading ? (
          <Skeleton width="100%" height={140} borderRadius={RADIUS.lg} />
        ) : payoutsError ? (
          <ErrorCard message={t('errors.generic')} onRetry={refetchPayouts} colors={colors} />
        ) : payouts.length === 0 ? (
          <EmptyPanel icon={Receipt} hint={t('org_payouts.history_empty')} colors={colors} />
        ) : (
          <View
            style={{
              borderRadius: RADIUS.lg,
              overflow: 'hidden',
              borderWidth: 1,
              borderColor: colors.border,
              backgroundColor: colors.surface,
              ...WEB_SHADOWS.sm,
            }}
          >
            {/* Table header */}
            <View
              style={{
                flexDirection: 'row',
                paddingHorizontal: SPACING.md,
                paddingVertical: SPACING.sm,
                backgroundColor: colors.surfaceSubtle,
                borderBottomWidth: 1,
                borderBottomColor: colors.border,
              }}
            >
              <TH text={t('org_payouts.col_date')} flex={1.1} colors={colors} />
              <TH text={t('org_payouts.col_amount')} flex={1} align="right" colors={colors} />
              <TH text={t('org_payouts.col_status')} flex={0.9} align="right" colors={colors} />
            </View>
            {payouts.map((p, idx) => (
              <View
                key={p.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: SPACING.md,
                  paddingVertical: SPACING.sm + 2,
                  borderBottomWidth: idx === payouts.length - 1 ? 0 : 1,
                  borderBottomColor: colors.border,
                  backgroundColor: idx % 2 === 1 ? colors.surfaceSubtle : colors.surface,
                }}
              >
                <Text
                  style={{
                    flex: 1.1,
                    fontSize: 13,
                    fontFamily: FONTS.body,
                    color: colors.textPrimary,
                  }}
                  numberOfLines={1}
                >
                  {formatDate(p.initiatedAt, i18n.language)}
                </Text>
                <Text
                  style={{
                    flex: 1,
                    fontSize: 13,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                    textAlign: 'right',
                  }}
                >
                  {formatCurrency(p.amountCents)}
                </Text>
                <View style={{ flex: 0.9, alignItems: 'flex-end' }}>
                  <PayoutStatusPill status={p.status} colors={colors} t={t} />
                </View>
              </View>
            ))}
          </View>
        )}

        {orgName ? (
          <Text
            style={{
              fontSize: 12,
              fontFamily: FONTS.body,
              color: colors.textMuted,
              textAlign: 'center',
              marginTop: SPACING.lg,
              lineHeight: 17,
            }}
          >
            {t('org_payouts.footnote', { name: orgName })}
          </Text>
        ) : null}
      </ScrollView>
    </ScreenContainer>
  )
}

/**
 * Every section header on this screen sits on the same step. The two headers
 * used to carry different top margins (none vs `2xl`), so identical labels
 * were read with double the leading on one of them.
 */
function SectionLabel({
  text,
  colors,
}: {
  text: string
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <Text
      style={{
        fontSize: 12,
        fontFamily: FONTS.body,
        color: colors.textMuted,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        marginTop: SPACING.xl,
        marginBottom: SPACING.sm,
      }}
    >
      {text}
    </Text>
  )
}

/**
 * The single empty treatment for this screen. There used to be two — a dashed
 * coral panel for the missing bank account and a filled grey box for the
 * missing payout history — neither of which matched the cards around them or
 * each other. One surface, one border, one icon chip; the actionable variant
 * is the same panel wrapped in a Pressable.
 */
function EmptyPanel({
  icon: Icon,
  title,
  hint,
  onPress,
  testID,
  colors,
}: {
  icon: typeof Wallet
  title?: string
  hint: string
  onPress?: () => void
  testID?: string
  colors: ReturnType<typeof useSemanticColors>
}) {
  const body = (
    <>
      <View
        style={{
          width: 48,
          height: 48,
          borderRadius: RADIUS.full,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${COLORS.coral.DEFAULT}14`,
        }}
      >
        <Icon size={24} color={COLORS.coral.DEFAULT} strokeWidth={1.5} />
      </View>
      {title ? (
        <Text
          style={{
            fontSize: 15,
            fontFamily: FONTS.bodyBold,
            color: colors.textAccent,
            textAlign: 'center',
          }}
        >
          {title}
        </Text>
      ) : null}
      <Text
        style={{
          fontSize: 13,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          textAlign: 'center',
          lineHeight: 18,
          maxWidth: 320,
        }}
      >
        {hint}
      </Text>
    </>
  )

  const panelStyle = {
    padding: SPACING.xl,
    borderRadius: RADIUS.lg,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center' as const,
    gap: SPACING.sm,
  }

  if (!onPress) {
    return <View style={panelStyle}>{body}</View>
  }

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={title ? `${title}. ${hint}` : hint}
      testID={testID}
      style={panelStyle}
    >
      {body}
    </Pressable>
  )
}

function EarningsTile({
  label,
  value,
  sublabel,
  loading,
  colors,
}: {
  label: string
  /** `null` when the earnings query has not resolved — rendered as a muted
   *  "no data" dash rather than a full-size em dash, which at 22px read as a
   *  horizontal rule four times across the grid. */
  value: string | null
  sublabel?: string
  loading?: boolean
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <View
      style={{
        flex: 1,
        minWidth: 140,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
      }}
    >
      <Text
        style={{
          // Was 10px uppercase Montserrat-Bold at 0.9 tracking — the least
          // legible combination in the file, on the screen that shows money.
          fontSize: 12,
          fontFamily: FONTS.body,
          color: colors.textMuted,
          marginBottom: 4,
        }}
      >
        {label}
      </Text>
      {loading ? (
        <Skeleton width={90} height={28} />
      ) : value === null ? (
        <Text
          style={{
            fontSize: 14,
            lineHeight: 28,
            fontFamily: FONTS.body,
            color: colors.textMuted,
          }}
        >
          —
        </Text>
      ) : (
        <Text
          style={{
            fontSize: 22,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
            letterSpacing: -0.3,
          }}
          numberOfLines={1}
          adjustsFontSizeToFit
        >
          {value}
        </Text>
      )}
      {sublabel && !loading ? (
        <Text
          style={{
            fontSize: 12,
            fontFamily: FONTS.body,
            color: colors.textMuted,
            marginTop: 2,
          }}
        >
          {sublabel}
        </Text>
      ) : null}
    </View>
  )
}

function AccountCard({
  bankName,
  accountHolder,
  masked,
  verified,
  onEdit,
  colors,
  t,
}: {
  bankName: string
  accountHolder: string
  masked: string
  verified: boolean
  onEdit: () => void
  colors: ReturnType<typeof useSemanticColors>
  t: ReturnType<typeof useTranslation>['t']
}) {
  return (
    <View
      style={{
        padding: SPACING.lg,
        borderRadius: RADIUS.lg,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        ...WEB_SHADOWS.sm,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: RADIUS.md,
            backgroundColor: verified ? `${colors.success}12` : colors.surfaceSubtle,
            borderWidth: 1,
            borderColor: verified ? `${colors.success}22` : colors.border,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Building2
            size={22}
            color={verified ? colors.success : colors.textSecondary}
            strokeWidth={1.5}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Text
            style={{
              fontSize: 15,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
            numberOfLines={1}
          >
            {bankName}
          </Text>
          <Text
            style={{
              fontSize: 13,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              marginTop: 2,
              fontVariant: ['tabular-nums'],
            }}
          >
            {masked}
          </Text>
          <Text
            style={{
              fontSize: 12,
              fontFamily: FONTS.body,
              color: colors.textMuted,
              marginTop: 2,
            }}
            numberOfLines={1}
          >
            {accountHolder}
          </Text>
        </View>
        {verified ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
              backgroundColor: `${colors.success}14`,
              paddingHorizontal: SPACING.sm,
              paddingVertical: 4,
              borderRadius: RADIUS.full,
            }}
          >
            <CheckCircle2 size={12} color={colors.success} strokeWidth={2} />
            <Text
              style={{
                fontSize: 12,
                fontFamily: FONTS.body,
                color: colors.success,
              }}
            >
              {t('common.verified')}
            </Text>
          </View>
        ) : (
          <View
            style={{
              backgroundColor: `${colors.warning}14`,
              paddingHorizontal: SPACING.sm,
              paddingVertical: 4,
              borderRadius: RADIUS.full,
            }}
          >
            <Text
              style={{
                fontSize: 12,
                fontFamily: FONTS.body,
                color: colors.warning,
              }}
            >
              {t('common.pending')}
            </Text>
          </View>
        )}
      </View>
      <Pressable
        onPress={onEdit}
        accessibilityRole="button"
        testID="org-payouts.edit-account"
        hitSlop={INTERACTION.iconHitSlop}
        style={{
          marginTop: SPACING.md,
          paddingVertical: SPACING.xs,
          alignItems: 'center',
          borderTopWidth: 1,
          borderTopColor: colors.border,
          paddingTop: SPACING.sm,
        }}
      >
        <Text style={{ fontSize: 13, fontFamily: FONTS.bodyBold, color: colors.textAccent }}>
          {t('org_payouts.edit_account')}
        </Text>
      </Pressable>
    </View>
  )
}

function PayoutStatusPill({
  status,
  colors,
  t,
}: {
  status: string
  colors: ReturnType<typeof useSemanticColors>
  t: ReturnType<typeof useTranslation>['t']
}) {
  const { bg, fg, labelKey } = (() => {
    switch (status) {
      case 'completed':
        return {
          bg: `${colors.success}14`,
          fg: colors.success,
          labelKey: 'org_payouts.status_completed',
        }
      case 'processing':
        return {
          bg: `${COLORS.amber.DEFAULT}18`,
          fg: COLORS.amber.dark,
          labelKey: 'org_payouts.status_processing',
        }
      case 'failed':
        return {
          bg: `${COLORS.coral.DEFAULT}14`,
          fg: COLORS.coral.DEFAULT,
          labelKey: 'org_payouts.status_failed',
        }
      case 'reversed':
        return {
          bg: colors.surfaceSubtle,
          fg: colors.textMuted,
          labelKey: 'org_payouts.status_reversed',
        }
      case 'pending':
      default:
        return {
          bg: colors.surfaceSubtle,
          fg: colors.textSecondary,
          labelKey: 'org_payouts.status_pending',
        }
    }
  })()
  return (
    <View
      style={{
        paddingHorizontal: SPACING.sm,
        paddingVertical: 3,
        borderRadius: RADIUS.full,
        backgroundColor: bg,
      }}
    >
      <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: fg }}>{t(labelKey)}</Text>
    </View>
  )
}

function ErrorCard({
  message,
  onRetry,
  colors,
}: {
  message: string
  onRetry: () => void
  colors: ReturnType<typeof useSemanticColors>
}) {
  const { t } = useTranslation()
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
        backgroundColor: `${colors.error}10`,
        borderWidth: 1,
        borderColor: `${colors.error}24`,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
      }}
    >
      <XCircle size={20} color={colors.textError} />
      <Text style={{ flex: 1, fontSize: 13, fontFamily: FONTS.body, color: colors.textPrimary }}>
        {message}
      </Text>
      <Pressable
        onPress={onRetry}
        accessibilityRole="button"
        accessibilityLabel={t('common.retry')}
        hitSlop={8}
        style={{
          paddingHorizontal: SPACING.md,
          paddingVertical: SPACING.xs,
          borderRadius: RADIUS.full,
          backgroundColor: colors.surface,
        }}
      >
        <Text style={{ fontSize: 12, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
          {t('common.retry')}
        </Text>
      </Pressable>
    </View>
  )
}

function TH({
  text,
  flex,
  align = 'left',
  colors,
}: {
  text: string
  flex: number
  align?: 'left' | 'right'
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <Text
      style={{
        flex,
        // Column headers: 12px sentence-case beats 10px uppercase-tracked.
        fontSize: 12,
        fontFamily: FONTS.body,
        color: colors.textMuted,
        textAlign: align,
      }}
    >
      {text}
    </Text>
  )
}
